import { combineReducers } from 'redux';
import type { TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type {
    AssetContainerModel,
    GenerateTranscriptResponse,
    GenerativeMediaModels,
    MediaFolder,
    SlideBackgroundModel,
    SlideBackgroundModelTypeEnum,
    TranscriptAssetType,
} from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    MaxVideoDurationSeconds,
    SourceEnum,
    TaskResultMessageTypeEnum,
} from 'js/src/client';
import { getMillisFromSeconds } from 'js/src/components/common/asset-trimmer/common';
import { media_reducers_map } from 'js/src/data/media/media-reducers';
import type {
    MediaFoldersTable,
    ModifiedAssetGenerationData,
    ModifiedAssetGenerationResponse,
    SearchContextValueType,
    StoredAssetContainer,
    StoredFolderSearchState,
    StoredMediaFolder,
} from 'js/src/data/media/media-types';
import {
    EDITOR_DAM_TAB_CONTEXTS,
    EDITOR_MUSIC_TAB_CONTEXTS,
    EDITOR_VOICEOVER_TAB_CONTEXTS,
    readonlySlideBackgroundModelIsImageBackgroundModel,
    readonlySlideBackgroundModelIsVideoBackgroundModel,
    SEARCH_CONTEXT,
} from 'js/src/data/media/media-types';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import type { BackgroundAssetContainerModel, FileWithMetadata } from 'js/src/data/uploads';
import {
    assetContainerModelIsBackground,
    readonlyAssetContainerModelIsAudio,
    readonlyAssetContainerModelIsBackground,
    readonlyAssetContainerModelIsFont,
    readonlyAssetContainerModelIsIcon,
} from 'js/src/data/uploads';
import {
    type DeepReadonly,
    GET_VALID_AUDIO_MIME_TYPES,
    GET_VALID_ICON_MIME_TYPES,
    GET_VALID_IMAGE_MIME_TYPES,
    GET_VALID_VIDEO_MIME_TYPES,
} from 'js/src/libs/consts';
import { downloadUrlAsFile } from 'js/src/libs/download-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import UploadMediaController from 'js/src/libs/upload-media-controller';
import WebSocketClient from 'js/src/libs/websocket-client';

/**
 * Given a search context (ie. 'recommended-visuals') returns the corresponding asset type ('background')
 */
export function getAssetTypeForSearchContext(
    searchContext: SearchContextValueType,
): AssetContainerTypes {
    let category_type = AssetContainerTypes.BACKGROUND;

    if (
        (EDITOR_MUSIC_TAB_CONTEXTS as readonly string[]).includes(searchContext) ||
        (EDITOR_VOICEOVER_TAB_CONTEXTS as readonly string[]).includes(searchContext)
    ) {
        category_type = AssetContainerTypes.AUDIO;
    }
    // Check if it's an icon-specific context (recommended icons or uploaded elements)
    if (
        searchContext === SEARCH_CONTEXT.RECOMMENDED_ICONS ||
        searchContext === SEARCH_CONTEXT.UPLOADED_ELEMENTS
    ) {
        category_type = AssetContainerTypes.ICON;
    }
    return category_type;
}

/**
 * Prompt cap applied to AI media models whose config carries no `max_prompt_length`
 * (Flux and WAN, whose providers document no limit).
 */
export const DEFAULT_MAX_PROMPT_LENGTH = 1000;

export function getMaxPromptLength(
    model: Pick<GenerativeMediaModels, 'max_prompt_length'> | null | undefined,
): number {
    return model?.max_prompt_length ?? DEFAULT_MAX_PROMPT_LENGTH;
}

export function isAnimatedSticker(thumbnailUrl: string | null | undefined): boolean {
    if (!thumbnailUrl) return false;
    const url = URL.parse(thumbnailUrl, window.location.origin);
    return url?.pathname.toLowerCase().endsWith('.gif') ?? false;
}

export function getIsFilterVisible(searchContext: SearchContextValueType): boolean {
    const categories_with_filters_enabled: SearchContextValueType[] = [
        SEARCH_CONTEXT.RECOMMENDED_VISUALS,
        SEARCH_CONTEXT.UPLOADED_VISUALS,
        SEARCH_CONTEXT.UPLOADED_ELEMENTS,
        SEARCH_CONTEXT.RECENT_VISUALS,
        SEARCH_CONTEXT.FAVORITE_VISUALS,
        SEARCH_CONTEXT.STOCK_AUDIO,
        SEARCH_CONTEXT.FREEPIK_STICKERS,
        ...EDITOR_DAM_TAB_CONTEXTS,
    ];
    return categories_with_filters_enabled.includes(searchContext);
}

export function getIsSuggestionsVisible(searchContext: SearchContextValueType): boolean {
    const disabled_categories: SearchContextValueType[] = [
        SEARCH_CONTEXT.UPLOADED_VOICEOVERS,
        SEARCH_CONTEXT.WAVEFORMS,
        ...EDITOR_DAM_TAB_CONTEXTS,
    ];
    return !disabled_categories.includes(searchContext);
}

export function getShouldResetSearchResultsOnLoad(searchContext: SearchContextValueType): boolean {
    const categories_that_should_reset_search_on_load: SearchContextValueType[] = [
        SEARCH_CONTEXT.FAVORITE_VISUALS,
        SEARCH_CONTEXT.FAVORITE_AUDIO,
    ];
    return categories_that_should_reset_search_on_load.includes(searchContext);
}

const MISSING_FOLDER_MESSAGE = 'getById: no entry exists. Did you mean to call getByIdOrUndefined?';

export function getStoredMediaFolderOrThrow(
    folders: MediaFoldersTable,
    folderId: number,
): StoredMediaFolder {
    const folder = new PlainJsStoreORM<StoredMediaFolder, number>(folders).getById(folderId);
    if (!folder) {
        throw new Error(MISSING_FOLDER_MESSAGE);
    }
    return folder;
}

function initialFolderSearchState(): StoredFolderSearchState {
    return combineReducers(media_reducers_map)(undefined, { type: 'INIT_FOLDERS' });
}

export function setUploadsFolderStore(folder: DeepReadonly<MediaFolder>): StoredMediaFolder {
    return { ...folder, ...initialFolderSearchState() };
}

export function resetUploadsFolderStore(folder: StoredMediaFolder): StoredMediaFolder {
    const initialized = initialFolderSearchState();
    const keys = Object.keys(initialized) as (keyof StoredFolderSearchState)[];
    return keys.every((key) => folder[key] === initialized[key])
        ? folder
        : { ...folder, ...initialized };
}

export function isAssetTypeMatchingSelectedAssetContainerType(
    asset: StoredAssetContainer,
    selectedContainerType: string | null,
    isSticker: boolean,
) {
    if (selectedContainerType === null) {
        return false;
    }
    if (isSticker) {
        return (
            (selectedContainerType === VideoData.MEDIA_ASSET_TYPE ||
                selectedContainerType === VideoData.ICON_ASSET_TYPE) &&
            (readonlyAssetContainerModelIsBackground(asset) ||
                readonlyAssetContainerModelIsIcon(asset))
        );
    }
    return (
        (selectedContainerType === VideoData.MEDIA_ASSET_TYPE &&
            readonlyAssetContainerModelIsBackground(asset)) ||
        (selectedContainerType === VideoData.ICON_ASSET_TYPE &&
            readonlyAssetContainerModelIsIcon(asset))
    );
}

export async function regenerateTranscriptForAsset(
    assetId: string,
    assetType: TranscriptAssetType,
    language_code: string | undefined,
) {
    const response = await ApiService.regenerateTranscriptForAssetCreate({
        body: {
            asset_uuid: assetId,
            asset_type: assetType,
            language_code,
        },
    });
    try {
        const ws = WebSocketClient.getInstance();
        const result = await ws.waitForMessage<GenerateTranscriptResponse>(
            response.data.task_id,
            TaskResultMessageTypeEnum.TASK_RESULT,
            'generate_transcript',
        );
        return result.has_voiceover;
    } catch (pollError) {
        throw new Error('Failed to determine voiceover status');
    }
}

export function getAssetUuidFromAssetContainer(
    assetContainer: StoredAssetContainer,
): string | null {
    if (readonlyAssetContainerModelIsBackground(assetContainer)) {
        return getAssetUuidFromBackgroundAsset(assetContainer[AssetContainerTypes.BACKGROUND]);
    }
    if (readonlyAssetContainerModelIsIcon(assetContainer)) {
        return assetContainer[AssetContainerTypes.ICON].uuid;
    }
    if (readonlyAssetContainerModelIsAudio(assetContainer)) {
        return assetContainer[AssetContainerTypes.AUDIO].uuid;
    }
    if (readonlyAssetContainerModelIsFont(assetContainer)) {
        return assetContainer[AssetContainerTypes.FONT].uuid;
    }
    return null;
}

export function getAssetUuidFromBackgroundAsset(
    backgroundAsset: DeepReadonly<SlideBackgroundModel>,
): string | null {
    if (readonlySlideBackgroundModelIsVideoBackgroundModel(backgroundAsset)) {
        return backgroundAsset.video?.uuid || null;
    }
    if (readonlySlideBackgroundModelIsImageBackgroundModel(backgroundAsset)) {
        return backgroundAsset.image?.uuid || null;
    }
    return null;
}

export function isDAMSearchContext(searchContext: SearchContextValueType): boolean {
    return searchContext.includes('dam-');
}

export const talkingHeadPlaybackRegionRequiresTrimming = (mediaAsset: TimeAwareMediaAsset) => {
    // use endTime - startTime instead of duration to check if the playback region >10mins
    // if it is, the user wouldn't be able to publish the video, so we should prompt them to trim
    const playbackDurationMillis = (mediaAsset.endTime as number) - mediaAsset.startTime;
    const maxTrimDurationMillis = getMillisFromSeconds(MaxVideoDurationSeconds.LIMIT);
    if (playbackDurationMillis > maxTrimDurationMillis) {
        return true;
    }
    return false;
};

export function checkAssetUUID(uuid: string | null | undefined) {
    if (uuid === null || uuid === undefined) {
        throw new Error('Asset UUID is null');
    }
    return uuid;
}

export function findGenerationInList(
    generations: ModifiedAssetGenerationResponse[],
    mediaUuid: string,
    mediaType: SlideBackgroundModelTypeEnum,
) {
    if (!mediaUuid) {
        return null;
    }
    const index = generations.findIndex((generation) => {
        if (generation?.result?.asset) {
            const asset = generation.result.asset;
            if (!assetContainerModelIsBackground(asset)) {
                throw new Error('Expected asset to be a background');
            }
            return asset.background?.[mediaType]?.uuid === mediaUuid;
        }
        return false;
    });

    if (index !== -1) {
        const gen = generations[index]?.result;
        if (gen) {
            return { ...gen, index } as typeof gen & { index: number };
        }
    }
    return null;
}

export async function saveAIMedia(generation: ModifiedAssetGenerationData) {
    const { uuid: generationUuid, prompt } = generation;
    const mediaAsset = generation?.asset?.background?.image;
    const url = mediaAsset?.preview_url;
    const uuid = mediaAsset?.uuid;
    if (!url || !uuid) {
        throw new Error('No valid AI media found');
    }

    const file = await downloadUrlAsFile({ url, fileName: '' });
    return new Promise<DeepReadonly<BackgroundAssetContainerModel>>((resolve, reject) => {
        // Upload the file to the bucket
        const mediaUploader = new UploadMediaController({
            file_list: [{ file }],
            asset_type: AssetContainerTypes.BACKGROUND,
            metadata: {
                uuid,
                source: SourceEnum.SOURCE_AI_GENERATED,
                source_id: url,
                license: VideoData.USER_PRIVATE_LICENSE,
                generation_uuid: generationUuid,
                description: prompt,
            },
            upload_succeeded_callback: (
                file: FileWithMetadata,
                newAsset: DeepReadonly<AssetContainerModel>,
            ) => {
                if (!newAsset || !readonlyAssetContainerModelIsBackground(newAsset)) {
                    throw new Error('AI image uploaded is not a background asset');
                }
                resolve(newAsset);
            },
            upload_failed_callback: (file: FileWithMetadata, message: string, e: Error) => {
                ErrorReporter.capture(e, { extra: { message, generationUuid } });
                reject(e);
            },
        });
        mediaUploader.upload();
    });
}

export function getAssetIsInDatabase(source: SourceEnum) {
    return ![
        SourceEnum.SOURCE_DAM,
        SourceEnum.SOURCE_TENOR,
        SourceEnum.SOURCE_AI_AVATAR,
        SourceEnum.SOURCE_FREEPIK,
    ].includes(source);
}

export function isVideoMimeType(mimeType: string): boolean {
    return GET_VALID_VIDEO_MIME_TYPES().includes(mimeType);
}

export function isImageMimeType(mimeType: string, only_images: boolean): boolean {
    return GET_VALID_IMAGE_MIME_TYPES(only_images).includes(mimeType);
}

export function isIconMimeType(mimeType: string): boolean {
    return GET_VALID_ICON_MIME_TYPES().includes(mimeType);
}

export function isAudioMimeType(mimeType: string): boolean {
    return GET_VALID_AUDIO_MIME_TYPES().includes(mimeType);
}
